import { type ReactNode } from "react";

interface Props {
  body_md: string;
}

/**
 * Renders briefing markdown as styled editorial elements.
 *
 * The OpenAI briefing prompt emits a constrained subset: H2/H3 headings,
 * unordered lists, paragraphs, and inline **bold** / *italic* / [links].
 * This component parses that subset and returns React nodes — raw markdown
 * syntax never reaches the DOM. No third-party markdown library; building
 * React nodes directly avoids any HTML-injection surface.
 */
export function BriefingBody({ body_md }: Props) {
  const blocks = parseBlocks(body_md);
  return (
    <div className="prose-brief space-y-4">
      {blocks.map((block, i) => renderBlock(block, i))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Block-level
// ---------------------------------------------------------------------------

type Block =
  | { type: "heading"; level: 2 | 3 | 4; content: string }
  | { type: "list"; items: string[] }
  | { type: "paragraph"; content: string }
  | { type: "blank" };

function parseBlocks(md: string): Block[] {
  const blocks: Block[] = [];
  const lines = md.split("\n");
  let i = 0;
  while (i < lines.length) {
    const raw = lines[i] ?? "";
    const line = raw.trimEnd();

    if (line.trim() === "") {
      i++;
      continue;
    }

    // Headings: ##, ###, ####
    const heading = line.match(/^(#{2,4})\s+(.*)$/);
    if (heading) {
      const level = Math.min(4, heading[1]!.length) as 2 | 3 | 4;
      blocks.push({ type: "heading", level, content: heading[2]! });
      i++;
      continue;
    }

    // Bullet list: collect consecutive `- ` / `* ` / `• ` lines
    if (/^(-|\*|•)\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^(-|\*|•)\s+/.test(lines[i]!.trim())) {
        items.push(lines[i]!.trim().replace(/^(-|\*|•)\s+/, ""));
        i++;
      }
      blocks.push({ type: "list", items });
      continue;
    }

    // Paragraph: collect non-blank, non-heading, non-list lines as one block
    const paragraph: string[] = [line];
    i++;
    while (
      i < lines.length &&
      lines[i]!.trim() !== "" &&
      !/^(#{2,4})\s+/.test(lines[i]!) &&
      !/^(-|\*|•)\s+/.test(lines[i]!.trim())
    ) {
      paragraph.push(lines[i]!.trim());
      i++;
    }
    blocks.push({ type: "paragraph", content: paragraph.join(" ") });
  }
  return blocks;
}

function renderBlock(block: Block, key: number): ReactNode {
  switch (block.type) {
    case "heading": {
      const cls =
        block.level === 2
          ? "font-display text-xl mt-6 mb-2 tracking-tight text-gold-glow"
          : block.level === 3
            ? "font-display text-base mt-5 mb-2 tracking-tight text-gold-glow uppercase"
            : "font-display text-sm mt-4 mb-1 tracking-tight text-fog uppercase";
      return (
        <p key={key} className={cls}>
          {parseInline(block.content)}
        </p>
      );
    }
    case "list":
      return (
        <ul key={key} className="space-y-2 pl-1">
          {block.items.map((it, j) => (
            <li
              key={j}
              className="text-fog/90 leading-relaxed pl-5 -indent-5 before:content-['·'] before:text-gold-glow before:mr-2 before:inline-block before:w-2"
            >
              {parseInline(it)}
            </li>
          ))}
        </ul>
      );
    case "paragraph":
      return (
        <p key={key} className="text-fog/90 leading-relaxed">
          {parseInline(block.content)}
        </p>
      );
    case "blank":
      return <div key={key} className="h-2" />;
  }
}

// ---------------------------------------------------------------------------
// Inline: **bold**, *italic*, [text](url), `code`
// Built as React nodes — never assembled as HTML strings, so no XSS surface.
// ---------------------------------------------------------------------------

function parseInline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let buf = "";
  let key = 0;
  const flush = () => {
    if (buf) {
      out.push(buf);
      buf = "";
    }
  };

  let i = 0;
  while (i < text.length) {
    // **bold**
    if (text[i] === "*" && text[i + 1] === "*") {
      const end = text.indexOf("**", i + 2);
      if (end > i + 2) {
        flush();
        out.push(<strong key={key++} className="text-fog">{parseInline(text.slice(i + 2, end))}</strong>);
        i = end + 2;
        continue;
      }
    }
    // [text](url)
    if (text[i] === "[") {
      const closeBracket = text.indexOf("]", i + 1);
      if (closeBracket > -1 && text[closeBracket + 1] === "(") {
        const closeParen = text.indexOf(")", closeBracket + 2);
        if (closeParen > -1) {
          const label = text.slice(i + 1, closeBracket);
          const href = text.slice(closeBracket + 2, closeParen);
          flush();
          out.push(
            <a
              key={key++}
              href={href}
              target="_blank"
              rel="noreferrer"
              className="text-gold-glow underline-offset-2 hover:underline"
            >
              {parseInline(label)}
            </a>,
          );
          i = closeParen + 1;
          continue;
        }
      }
    }
    // `code`
    if (text[i] === "`") {
      const end = text.indexOf("`", i + 1);
      if (end > i + 1) {
        flush();
        out.push(
          <code
            key={key++}
            className="px-1.5 py-0.5 rounded bg-veil/40 text-fog text-[0.92em] font-mono"
          >
            {text.slice(i + 1, end)}
          </code>,
        );
        i = end + 1;
        continue;
      }
    }
    // *italic* — single asterisk, word-bounded
    if (
      text[i] === "*" &&
      text[i + 1] !== "*" &&
      (i === 0 || /\s|\(|\[/.test(text[i - 1]!))
    ) {
      const end = text.indexOf("*", i + 1);
      if (end > i + 1 && text[end + 1] !== "*") {
        flush();
        out.push(<em key={key++}>{text.slice(i + 1, end)}</em>);
        i = end + 1;
        continue;
      }
    }
    buf += text[i];
    i++;
  }
  flush();
  return out;
}
